import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, Columns3, ExternalLink, Loader2, Search, X } from "lucide-react";

import PropertyPicker from "./PropertyPicker";
import { alignFor, renderCell } from "./charts/cells";
import { fetchObjectProperties, fetchReportRecords, updateReportDisplay } from "../lib/api";

/**
 * The records behind a board figure.
 *
 * Same idea as the MRR and ARR drilldowns: a number is only trustworthy if you
 * can open it and see what produced it. The columns are the properties the
 * report actually reads, so what you see is what decided the answer.
 */
export default function ReportDrilldownSheet({
  portalId,
  reportKey,
  title,
  group,
  open,
  onClose,
  fiscalYear = null,
  team = null,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ column: null, direction: "asc" });
  // Choosing columns happens here, where they are read, and saves to the
  // report so every drilldown on it opens with them.
  const [editingColumns, setEditingColumns] = useState(false);
  const [properties, setProperties] = useState([]);
  const [savingColumns, setSavingColumns] = useState(false);

  const load = useCallback(async () => {
    if (!open || !portalId || !reportKey) return;
    setLoading(true);
    setError(null);
    try {
      // The same year and team the card was run with, or the drilldown would
      // list records the figure never counted.
      setData(await fetchReportRecords(portalId, reportKey, group, { fiscalYear, team }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load the records.");
    } finally {
      setLoading(false);
    }
  }, [open, portalId, reportKey, group, fiscalYear, team]);

  useEffect(() => {
    load();
  }, [load]);

  // Escape closes, matching the other drawers in the app.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const columns = useMemo(() => data?.columns || [], [data]);

  useEffect(() => {
    if (!editingColumns || !portalId || !data?.source) return;
    fetchObjectProperties(portalId, data.source)
      .then((result) => setProperties(result.properties || []))
      .catch(() => setProperties([]));
  }, [editingColumns, portalId, data?.source]);

  const saveColumns = async (names) => {
    setSavingColumns(true);
    try {
      await updateReportDisplay(portalId, reportKey, { drilldown_columns: names });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save the columns.");
    } finally {
      setSavingColumns(false);
    }
  };

  // What is shown now, whether chosen or the report's defaults, so editing
  // starts from the columns on screen rather than from nothing.
  const shownColumns = useMemo(
    () => (data?.chosen_columns?.length ? data.chosen_columns : columns.map((c) => c.name))
      .filter((name) => !name.startsWith("__")),
    [data, columns],
  );

  // Search across every visible cell, so "churned" finds them whichever column
  // holds it, and the record name is searchable too.
  const rows = useMemo(() => {
    let list = data?.rows || [];
    const needle = query.trim().toLowerCase();
    if (needle) {
      list = list.filter((row) => {
        const haystack = [row.name, row.status, ...Object.values(row.cells || {})]
          .filter((v) => v !== null && v !== undefined)
          .join(" ")
          .toLowerCase();
        return haystack.includes(needle);
      });
    }
    if (!sort.column) return list;
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const left = sort.column === "__name__" ? a.name
        : sort.column === "__status__" ? a.status
        : a.cells?.[sort.column];
      const right = sort.column === "__name__" ? b.name
        : sort.column === "__status__" ? b.status
        : b.cells?.[sort.column];
      if (left === right) return 0;
      if (left === null || left === undefined) return 1;      // blanks last
      if (right === null || right === undefined) return -1;
      // Numbers compare numerically; everything else as text.
      if (typeof left === "number" && typeof right === "number") {
        return (left - right) * factor;
      }
      return String(left).localeCompare(String(right), "en-GB", { numeric: true }) * factor;
    });
  }, [data, query, sort]);

  const toggleSort = (column) =>
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );

  const SortHeader = ({ column, children, align = "text-left" }) => (
    <th className={`px-4 py-2.5 ${align}`}>
      <button
        type="button"
        onClick={() => toggleSort(column)}
        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4a5b68] hover:text-[#323c44]"
      >
        {children}
        {sort.column === column &&
          (sort.direction === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end print:hidden" data-testid="report-drilldown">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30"
      />
      <aside className="relative flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-[#cccccc] px-5 py-4">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#119996]">
              Contributing records
            </p>
            <h2 className="mt-1 truncate font-sans text-lg font-bold text-[#323c44]">{title}</h2>
            <p className="mt-0.5 text-sm text-[#4a5b68]">
              {group ? `${group} — ` : ""}
              {data ? `${data.total} record${data.total === 1 ? "" : "s"}` : "Loading…"}
              {data?.truncated ? `, showing the first ${data.rows.length}` : ""}
              {query && data ? ` · ${rows.length} matching` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-[#4a5b68] hover:bg-slate-100 hover:text-[#323c44]"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {data && (
          <div className="space-y-2 border-b border-[#efefef] px-5 py-2.5">
            <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-[#a9b3bb]" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter these records…"
                className="w-full rounded border border-[#cccccc] bg-white py-1.5 pl-8 pr-2.5 text-sm text-[#323c44] focus:border-[#57c1a9] focus:outline-none"
                data-testid="drilldown-filter"
              />
            </div>
            <button
              type="button"
              onClick={() => setEditingColumns((value) => !value)}
              className={`inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold ${
                editingColumns
                  ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                  : "border-[#cccccc] bg-white text-[#4a5b68] hover:border-[#57c1a9] hover:text-[#119996]"
              }`}
              data-testid="drilldown-columns-toggle"
            >
              <Columns3 className="h-3.5 w-3.5" /> Columns
            </button>
            </div>
            {editingColumns && (
              <div className="rounded border border-[#efefef] bg-[#f5f5f5] p-3">
                <PropertyPicker
                  properties={properties}
                  value={shownColumns}
                  onChange={saveColumns}
                  placeholder="Choose columns…"
                  multiple
                  testId="drilldown-column-picker"
                />
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {shownColumns.map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center gap-1 rounded bg-[#efefef] px-2 py-0.5 text-[11px] text-[#4a5b68]"
                    >
                      {properties.find((p) => p.name === name)?.label
                        || columns.find((c) => c.name === name)?.label
                        || name}
                      <button
                        type="button"
                        onClick={() => saveColumns(shownColumns.filter((c) => c !== name))}
                        className="text-[#a9b3bb] hover:text-red-600"
                        aria-label={`Remove ${name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {savingColumns && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#7d8b96]" />}
                </div>
                <p className="mt-1.5 text-[11px] text-[#7d8b96]">
                  Saved to this report, so its drilldowns open with these columns.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {loading && (
            <p className="flex items-center justify-center gap-2 py-16 text-sm text-[#4a5b68]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading records…
            </p>
          )}
          {error && (
            <p className="m-5 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {error}
            </p>
          )}
          {!loading && !error && data && (
            rows.length ? (
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-[#f5f5f5]">
                  <tr className="border-b border-[#cccccc]">
                    <SortHeader column="__name__">Record</SortHeader>
                    {data.has_status && <SortHeader column="__status__">Status</SortHeader>}
                    {columns.map((column) => (
                      <SortHeader key={column.name} column={column.name} align={alignFor(column)}>
                        {column.label}
                      </SortHeader>
                    ))}
                    <th className="w-10 px-4 py-2.5" aria-label="Open in HubSpot" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-[#efefef] last:border-0 hover:bg-[#f5f5f5]">
                      <td className="px-4 py-2.5 font-medium text-[#323c44]">{row.name}</td>
                      {data.has_status && (
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                              row.status === "Retained"
                                ? "bg-emerald-50 text-emerald-700"
                                : ["Leaving", "At risk", "Not renewing", "Unlikely to renew"].includes(row.status)
                                  // Live today, but with a churn date already
                                  // in the diary, or forecast not to renew.
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-red-50 text-red-700"
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                      )}
                      {columns.map((column) => (
                        <td key={column.name} className={`px-4 py-2.5 ${alignFor(column)}`}>
                          {renderCell(row.cells[column.name], column)}
                        </td>
                      ))}
                      <td className="px-4 py-2.5">
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#4a5b68] hover:text-[#119996]"
                          title="Open in HubSpot"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="py-16 text-center text-sm text-[#4a5b68]">
                {query ? "No records match that filter." : "No records in this selection."}
              </p>
            )
          )}
        </div>
      </aside>
    </div>
  );
}
